import type { AlertColor } from "@mui/material";
import { Alert, Container, Box, Button, Snackbar, Grid, Typography, CircularProgress } from "@mui/material";
import Stack from "@mui/material/Stack";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLoaderData } from "react-router-dom";

import CheckAccessModal from "./CheckAccessModal";
import TokenSignatureModal from "./TokenSignatureModal";
import type { CheckAccessResponse } from "../../../api/signer";
import { signatureSingleLink } from "../../../api/signer";
import urlToBase64 from "../../../utils/urlToBase64";
import Documents from "../../sign/components/documents";
import Info from "../../sign/components/info";
import LinkCodeHasExpired from "../../sign/components/LinkCodeHasExpired";

function SingleSigner(): React.JSX.Element {
  const { t } = useTranslation();
  const { linkCode } = useLoaderData() as { linkCode: string };

  const [signerData, setSignerData] = useState<ResponseSignatures | null>(null);
  const [checkAccessOpen, setCheckAccessOpen] = useState(true);
  const [verifiedSignerId, setVerifiedSignerId] = useState("");
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);

  const [documentSelected, setDocumentSelected] = useState<FileSigner>(
    {} as FileSigner,
  );
  const [messageAlert, setMessageAlert] = useState("");
  const [typeAlert, setTypeAlert] = useState<AlertColor>("info");
  const [openAlert, setOpenAlert] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const loadDocument = useCallback(async (): Promise<void> => {
    if (!linkCode) return;
    setIsLoadingDocument(true);
    try {
      const { data } = await signatureSingleLink(linkCode);
      setSignerData({ ...data, hasExpired: false });
      const targetFile =
        data?.files && data.files.length > 0
          ? data.files[0]
          : data?.file
          ? data.file
          : ({} as FileSigner);
      setDocumentSelected(targetFile);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 400) {
          const responseData = e.response.data as { objects?: ResponseObjectsError[] } | undefined;
          setSignerData({
            hasExpired: true,
            objects: responseData?.objects,
          } as ResponseSignatures);
        }
      }
    } finally {
      setIsLoadingDocument(false);
    }
  }, [linkCode]);

  const handleCheckAccessSuccess = (res: CheckAccessResponse): void => {
    setVerifiedSignerId(res.signerId);
    setCheckAccessOpen(false);
    void loadDocument();
  };

  useEffect(() => {
    const targetFile =
      signerData?.files && signerData.files.length > 0
        ? signerData.files[0]
        : signerData?.file;

    if (targetFile) {
      setDocumentSelected(targetFile);
    }

    const firstDocumentUrl = targetFile?.temporaryUrl;
    if (firstDocumentUrl) {
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.href = firstDocumentUrl;
      link.as = "fetch";
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);

      return () => {
        document.head.removeChild(link);
      };
    }
  }, [signerData?.files, signerData?.file]);

  const handleOpenModal = (): void => {
    setOpenModal(true);
  };

  const handleCloseModal = (): void => {
    setOpenModal(false);
  };

  const handleViewDocument = (doc: FileSigner): void => {
    setDocumentSelected(doc);
  };

  const handleCloseAlert = (): void => {
    setOpenAlert(false);
  };

  const handleCloseWindow = (): void => {
    const newWindow = window.open("", "_self");
    newWindow?.close();
  };

  const currentSigner = signerData
    ? signerData.signers.find((signer) => signer.id === (verifiedSignerId || signerData.currentSignerId)) ||
      signerData.signers.find((signer) => signer.id === signerData.currentSignerId) ||
      signerData.signers[0]
    : undefined;

  const signerIdentifier = currentSigner?.email || currentSigner?.document || "";
  const isAlreadySigned = currentSigner?.status === "SIGNED" || signerData?.status === "SIGNED";

  const isSignButtonEnabled =
    currentSigner?.status === "PENDING" &&
    Boolean(verifiedSignerId) &&
    (signerData?.currentSignerId === verifiedSignerId || currentSigner.id === verifiedSignerId);

  const renderMainContent = (): React.JSX.Element | null => {
    if (checkAccessOpen || isLoadingDocument) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "75vh",
          }}
        >
          {isLoadingDocument ? <CircularProgress size={40} /> : null}
        </Box>
      );
    }

    if (signerData?.hasExpired) {
      return <LinkCodeHasExpired info={signerData.objects} />;
    }

    if (signerData) {
      return (
        <Grid container spacing={3} sx={{ minHeight: "85vh" }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Info
              authorName={signerData.authorName}
              createdAt={signerData.createdAt}
              id={signerData.id}
              status={signerData.status}
              title={signerData.title}
              updatedAt={signerData.updatedAt}
            />

            <Documents
              documentSelectedId={documentSelected.id || ""}
              documents={
                signerData.files && signerData.files.length > 0
                  ? signerData.files
                  : signerData.file
                  ? [signerData.file]
                  : []
              }
              handleViewDocument={handleViewDocument}
            />

            {isAlreadySigned ? (
              <Alert severity="success" sx={{ mt: 6, borderRadius: "12px" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t("chooseSignature.alreadySigned", "Este documento já foi assinado.")}
                </Typography>
                {currentSigner?.name ? (
                  <Typography variant="body2">
                    Assinado por: {currentSigner.name} ({currentSigner.email})
                  </Typography>
                ) : null}
                {currentSigner?.updatedAt ? (
                  <Typography variant="body2">
                    Data/Hora: {new Date(currentSigner.updatedAt).toLocaleString()}
                  </Typography>
                ) : null}
              </Alert>
            ) : (
              <Stack direction="column" spacing={2} sx={{ mt: 6 }}>
                <Button
                  color="primary"
                  disabled={!isSignButtonEnabled}
                  onClick={handleOpenModal}
                  style={{ width: "100%", borderRadius: "12px" }}
                  variant="contained"
                >
                  {t("sign.info.signButton", "Assinar Documento")}
                </Button>
                <Button
                  color="primary"
                  disabled={!isSignButtonEnabled}
                  onClick={handleCloseWindow}
                  style={{ width: "100%", borderRadius: "12px" }}
                  variant="outlined"
                >
                  {t("sign.info.signLater", "Assinar Mais Tarde")}
                </Button>
              </Stack>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {documentSelected?.temporaryUrl ? (
              <iframe
                height="100%"
                src={`https://pdf-viewer.smartdocs.com.br/web/viewer.html?file=${urlToBase64(
                  documentSelected.temporaryUrl,
                )}&pdfBug=disable&externalLinkTarget=0`}
                style={{
                  border: "none",
                }}
                title={documentSelected.filename || "Document Viewer"}
                width="100%"
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  minHeight: "400px",
                  bgcolor: "background.paper",
                  borderRadius: "8px",
                  border: "1px dashed",
                  borderColor: "divider",
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Container>
        <CheckAccessModal
          linkCode={linkCode}
          onSuccess={handleCheckAccessSuccess}
          open={checkAccessOpen}
        />

        {renderMainContent()}

        {signerData ? (
          <TokenSignatureModal
            id={signerData.id}
            onClose={handleCloseModal}
            onSuccess={() => {
              void loadDocument();
            }}
            open={openModal}
            setMessageAlert={setMessageAlert}
            setOpenAlert={setOpenAlert}
            setTypeAlert={setTypeAlert}
            signerIdentifier={signerIdentifier}
          />
        ) : null}

        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          autoHideDuration={5000}
          onClose={handleCloseAlert}
          open={openAlert}
        >
          <Alert
            onClose={handleCloseAlert}
            severity={typeAlert}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {messageAlert}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}

export default SingleSigner;
